import { useMemo, useState } from "react";

export type SortDirection = "asc" | "desc" | null;

export type SortState = {
  column: string | null;
  direction: SortDirection;
};

export type UseSortingReturn = {
  sortState: SortState;
  handleSort: (columnId: string) => void;
  getSortedData: <T>(
    data: T[],
    columns: Array<{ id: string; accessor: string | ((item: T) => unknown) }>
  ) => T[];
};

export const useSorting = (initialSort?: SortState): UseSortingReturn => {
  const [sortState, setSortState] = useState<SortState>(
    initialSort || { column: null, direction: null }
  );

  const handleSort = (columnId: string) => {
    setSortState(prevState => {
      if (prevState.column === columnId) {
        // Same column clicked - cycle through: asc -> desc -> null
        if (prevState.direction === "asc") {
          return { column: columnId, direction: "desc" };
        } else if (prevState.direction === "desc") {
          return { column: null, direction: null };
        } else {
          return { column: columnId, direction: "asc" };
        }
      } else {
        // Different column clicked - start with asc
        return { column: columnId, direction: "asc" };
      }
    });
  };

  const getSortedData = useMemo(() => {
    return <T>(
      data: T[],
      columns: Array<{ id: string; accessor: string | ((item: T) => unknown) }>
    ) => {
      if (!sortState.column || !sortState.direction) {
        return data;
      }

      const column = columns.find(col => col.id === sortState.column);
      if (!column) {
        return data;
      }

      const sortedData = [...data].sort((a, b) => {
        let aValue: unknown;
        let bValue: unknown;

        if (typeof column.accessor === "function") {
          aValue = column.accessor(a);
          bValue = column.accessor(b);
        } else {
          // Handle nested object access
          const keys = column.accessor.split(".");
          aValue = keys.reduce((obj: unknown, key) => {
            return (obj as Record<string, unknown>)?.[key];
          }, a);
          bValue = keys.reduce((obj: unknown, key) => {
            return (obj as Record<string, unknown>)?.[key];
          }, b);
        }

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        // Convert to string for comparison if both values exist
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        // Try to parse as numbers for numeric sorting
        const aNum = Number(aValue);
        const bNum = Number(bValue);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortState.direction === "asc" ? aNum - bNum : bNum - aNum;
        }

        // Try to parse as dates
        const aDate = new Date(aValue as string);
        const bDate = new Date(bValue as string);

        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return sortState.direction === "asc"
            ? aDate.getTime() - bDate.getTime()
            : bDate.getTime() - aDate.getTime();
        }

        // Default string comparison
        if (sortState.direction === "asc") {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });

      return sortedData;
    };
  }, [sortState]);

  return {
    sortState,
    handleSort,
    getSortedData,
  };
};
